import React from "react";
import { MetaTags } from "react-meta-tags";
import { getLink, getWebpImage } from "../../../../Common/functions";
import "../../BlogCSS/styles.css";

const makeList = (listArray) => {
  return (
    <ul>
      {listArray?.map((ele, index) => (
        <li key={index}>{ele}</li>
      ))}
    </ul>
  );
};

function Blog_56() {
  return (
    <div className="blog-main-container">
      <MetaTags>
        <title>
          Common BRSR Reporting Mistakes & How Companies Can Avoid Them
        </title>
        <meta
          name="description"
          content="Learn the most common BRSR reporting mistakes companies make and how to avoid them. Improve ESG reporting accuracy and meet SEBI BRSR compliance requirements."
        />
        <meta
          property="og:title"
          content="UpdaptCSR | Sustainability Platform"
        />
      </MetaTags>
      <div className="blog-title">
        Common BRSR Reporting Mistakes and How to Avoid Them
      </div>
      <div className="blog-author-details">
        <div className="blog-author-right">
          <div className="blog-author-top">
            <div className="blog-author-name">Subash M</div>
          </div>
          <div className="blog-author-bottom">
            <div className="blog-month">January 27, 2026</div>
            <div className="blog-read-time">7 min read</div>
          </div>
        </div>
      </div>
      <p>
        {getWebpImage(
          "blog_56.webp",
          "Common BRSR Reporting Mistakes and How to Avoid Them"
        )}
      </p>
      <p>
        Since the time SEBI has introduced Business Responsibility and
        Sustainability Reporting (BRSR), ESG disclosure has transformed from a
        voluntary initiative to being a compliance requirement. BRSR is no
        longer an optional compliance, it is a regulatory necessity for the top
        1,000 listed companies especially. <br />
        But with companies desperately needing to meet SEBI BRSR requirements,
        many might get into unavoidable pitfalls that question the quality,
        usefulness, and credibility of their reporting. Unacceptable BRSR
        reports will not only risk regulatory compilations but also hamper
        stakeholder trust, brand reputation, and investor confidence.
        <br />
        This blog talks about most commonly BRSR reporting mistakes committed by
        Indian organizations and provides practical, actionable guidance of
        their avoidance in order to help them strengthen the{" "}
        {getLink("ESG reporting of India", "/solutions/esg-reporting")}
      </p>
      <p>
        <div className="blog-sub-heading">
          Understanding the Importance of Accuracy in BRSR
        </div>
        BRSR is based on the National Guidelines on Responsible Business Conduct
        (NGRBC) and works on the nine core principles including labor practices,
        community engagement, human rights, governance, environment, and
        consumer responsibility.
        <br />
        BRSR also demands:
        <ul>
          <li>Policy-linked disclosure</li>
          <li>Outcome-led reporting</li>
          <li>Quantitative metrics</li>
          <li>Proof of implementation</li>
        </ul>
        The common mistakes in BRSR often come when the companies treat it as a
        documentation exercise instead of a strategic ESG framework.
      </p>
      <p>
        <b>Mistake 1 : Assuming BRSR as a Compliance Checkbox</b>
        <br />
        The most common mistake is treating BRSR reporting as an end-moment
        compliance work taken care of only by a secretarial or legal team. This
        results in general responses, poor data quality, and superficial
        disclosure.
        <br />
        <br />
        It usually happens because:
        {makeList([
          "Constrained ownership of leadership",
          "ESG is not inculcated into the core business strategy",
          "Departments working in silos",
        ])}
        <br />
        However, it can be avoided in following by:
        {makeList([
          "A cluster of leadership, Human Resource, Operations, Procurement, and finance teams.",
          "Appointing a dedicated sustainability lead with cross-functional authority.",
          "Position BRSR as a business responsibility framework and not just a SEBI requirement.",
        ])}
      </p>
      <p>
        <b>Mistake 2 : Incompatible of Incorrect Data Reporting</b>
        <br />
        Most of the organizations submit incompatible ESG data in various
        different sections of the BRSR report. The environment numbers do not
        comply with sustainability claims, or workforce data disagree with HR
        disclosures.
        <br />
        It usually happens because of:
        {makeList([
          "Manual data collection",
          "Bad internal validation",
          "Lack of centralized ESG data systems",
        ])}
        <br />
        However, it can be avoided in following by:
        {makeList([
          "Standardizing data explanations and reporting constraints.",
          <>
            Leveraging
            {getLink(" ESG reporting softwares", "/solutions/esg-reporting")} or
            structured spreadsheets.
          </>,
          "Creating an individual ESG data repository",
          "Conducting internal audits before its submission",
        ])}
        Hence, accurate data is the backbone of BRSR reporting.
      </p>
      <p>
        <b>Mistake 3 : Over-Dependence on Qualitative Statements</b>
        <br />
        Major companies have their BRSR reports filled with statements like “The
        company is committed to sustainability”. Whereas, intent is important,
        SEBI BRSR requirements emphasize measurable outcomes and not only
        commitments. <br />
        It happens because of:
        {makeList([
          "Limited ESG measurement systems",
          "Conflict between CSR narratives and BRSR expectations",
          "Fear of transferring weak or incomplete metrics",
        ])}
        <br />
        It can be avoided by:
        {makeList([
          `Reporting the actual performance, even if it's not considered perfect as per the standards.`,
          "Clearly specifying improvement plans and timelines.",
          "Balancing qualitative explanations with the quantitative measures. ",
        ])}
        Hence, transparency matters more than the perfection in the{" "}
        {getLink("ESG reporting", "/solutions/esg-reporting")} in India.
      </p>
      <p>
        <b>Mistake 4 : Using ESG Disclosures as it is:</b>
        <br />
        Few companies reuse ESG disclosures from the annual reports,
        sustainability reports, or even other companies without even aligning
        them to the BRSR framework. <br />
        It usually happens because of:
        {makeList([
          "Constrained ESG expertise",
          "Time pressure",
          "Misunderstanding of BRSR structure ",
        ])}
        <br />
        It can be avoided by:
        {makeList([
          "Mapping existing disclosures to specific BRSR questions",
          "Customizing responses based on business operations",
          "Avoiding generic language that lacks relevance.",
        ])}
        BRSR depends on the company and is not a template- based exercise.
      </p>
      <p>
        <b>Mistake 5 : Disregard Scope and Boundary Definitions</b>
        <br />
        Companies usually fail to define subsidiaries covered, value chain
        inclusion, and Operational Boundaries. This results in creating
        confusion and reduction of report credibility. <br />
        It mostly happens due to:
        {makeList([
          "Complicated group structures",
          "Unclear reporting scope",
          "Improper internal ESG governance",
        ])}
        <br />
        But it can be avoided by:
        {makeList([
          "Maintaining consistency across different sections",
          "Disclosure of exclusions transparently with reasons",
          "Clearly stating reporting scope at the beginning",
        ])}
        Hence defined and clear boundaries enhance trust with BRSR compliance.
      </p>
      <p>
        <b>Mistake 6 : Poor Environmental Metrics and Targets</b>
        <br />
        Environmental disclosures are usually inconsistent, incomplete, or lack
        future targets specifically around emissions, water use, and waste
        management. <br />
        It happens because of:
        {makeList([
          "Regulatory scrutiny fear",
          "Limited experience in climate reporting",
          "Unavailability of environmental monitoring systems",
        ])}
        <br />
        But, it can be avoided by:
        {makeList([
          "Aligning by national and global climate goals",
          "Setting achievable short-term and long-term targets ",
          "Tracking basic environmental KPIs: energy, water, waste. emissions",
        ])}
        Strong data strengthens the{" "}
        {getLink("ESG reporting India", "/solutions/esg-reporting")} and
        investor trust.
      </p>

      <p>
        <b>Mistake 7 : Scarce social and human rights reporting</b>
        <br />
        Companies do not report workforce diversity, grievance redressal
        outcomes and health safety metrics. Hence, the social results are mostly
        descriptive and not outcome-focused.
        <br />
        It happens due to:
        {makeList([
          "Improper data collection on supply chain workers",
          "Cultural sensitiveness about disclosures",
        ])}
        <br />
        But it can be avoided by:
        {makeList([
          "Presenting the report to key contractors",
          "Disclosing grievance numbers, outcomes, and resolution dates",
          "Reporting workforce data transparently",
        ])}
        Hence, Social in ESG is as important as Ethical.
      </p>
      <p>
        <b>Mistake 8 : Ignoring the Value Chain and Supplier Disclosures</b>
        <br />
        Most of the companies restrict the BRSR reporting to inside operations
        and do not take ESG risks seriously.
        <br />
        It majorly happens due to:
        {makeList([
          "Poor procurement governance",
          "Limited supplier ESG policies",
        ])}
        <br />
        It can also be avoided by:
        {makeList([
          "Introducing supplier rules and regulations",
          "Practice basic ESG risk assessments",
          "Steadily expand the value chain disclosures.",
        ])}
        SEBI BRSR requirements focus on responsible supply chains.
      </p>
      <p>
        <b>Mistake 9 : Improper Governance and Ethics declaration</b>
        <br />
        The governance declarations are mostly unclear, specifically around
        Anti-corruption measures, Whistleblower mechanisms, and Board-level ESG
        oversight.
        <br />
        It happens because:
        {makeList([
          "Lack of proper documentation",
          "Uncomfortable governance failure disclosures.",
        ])}
        <br />
        But it can be avoided by:
        {makeList([
          "Specifying the involvement of board in ESG strategy",
          "Disclosing ethics training, complaints received, and actions to be taken.",
          "Specifically describing governance structures and accountability.",
        ])}
        Hence, a strong governance enhances credibility of full BRSR reports.
      </p>
      <p>
        <b>Mistake 10 : Not including External or Internal Review Process</b>
        <br />
        BRSR reports are often delivered without independent review, leading to
        risk of inconsistencies and errors. <br />
        It usually happens due to:
        {makeList(["Time restrictions", "Const concerns"])}
        <br />
        But it can be avoided by:
        {makeList([
          "Benchmarking against peers",
          "Engaging third-party ESG consultants for the review",
          "Conducting internal ESG audits",
        ])}
        In conclusion, the verification improves trust in the stakeholders.
      </p>
      <p>
        <b>Mistake 11 : No Continuous Improvement Mindset</b>
        <br />
        Companies often treat BRSR as a yearly exercise, and not a continuous
        improvement.
        <br />
        It occurs because:
        {makeList([
          "No continuous ESG tracking in short intervals.",
          "Not linking ESG to business KPIs.",
        ])}
        <br />
        Instead, it can be avoided by:
        {makeList([
          "Using BRSR insights in strategic decision-making.",
          "Inculcate ESG goals with business planning.",
          <>
            Regularly tracking{" "}
            {getLink("ESG metrics", "/solutions/esg-data-management")}
          </>,
        ])}
        Hence, BRSR should tend to evolve with business.
      </p>
      <p>
        <div className="blog-sub-heading">
          Best Practices for Strong BRSR Compliance
        </div>
        Strong BRSR compliance needs a strategic approach more than mere
        regulatory compliance. BRSR was indeed introduced as a compliance
        framework by SEBI, but such organizations that have approached it merely
        from a tick-box perspective have to fight with data gaps,
        inconsistencies, and credibility issues more frequently. Companies
        following best practices in reporting through the BRSR are better
        equipped to generate long-term value while simultaneously reinforcing
        the trust of stakeholders.
        <br />
        <br />
        {makeList(["Start Early and Plan Proactively"])}
        Early planning is critical to effective BRSR compliance. Since the
        format of BRSR requires data collection across multiple functions, such
        as HR, operations, finance, procurement, and governance, last-minute
        efforts are always rushed with incomplete disclosures. Proactive
        planning helps an organization in early identification of data gaps,
        definition of reporting boundaries clearly, and assignment of
        responsibilities well in time. Embedding the BRSR planning into the
        annual business calendar can bring better data quality, less pressure on
        compliance, and smoother execution for the company.
        <br />
        <br />
        {makeList(["Build Internal ESG Capabilities"])}
        Building internal capability is a vital area for effective BRSR
        reporting. For instance, most organizations have struggled over the
        years since ESG responsibilities are scattered or allocated to teams
        that lack the necessary expertise. Building internal expertise for ESG
        means appointing a dedicated sustainability officer for the
        organization. For instance, when all the employees are aware of ESG
        relevance to business outcomes, it makes for a more accurate BRSR
        report.
        <br />
        <br />
        {makeList(["Apply Structured Data Systems"])}
        Good data is the backbone for reliable ESG reporting in India, and
        manual methods of gathering such data also pose a significant threat of
        inconsistencies and errors in BRSR reports. Having an effective and
        organized system of data will not only help an organization streamline
        its metrics and make them standardized; the traceability and consistency
        will also remain beneficial while preparing the reports for the
        organization. This will remain a challenge if such a reporting software
        is not available and if reliable internal audits can be obtained.
        <br />
        <br />
        {makeList(["Consider incorporating Transparency and Honesty"])}
        A major component of BRSR is transparency. Although a company may shy
        away from presenting areas of poor performance, SEBI emphasizes how
        honesty is more important than perfection. When we talk about
        transparency, we refer to the actual performance, challenges, and
        realistic plans and timelines to improve those challenges. Attributable
        to their honesty, stakeholders admire and respect such organizations.
        <br />
        <br />
        {makeList(["Align ESG Goals with Business Strategy"])}
        However, the most effective use of the BRSR reporting would be achieved
        when there are integrations of the ESG goals with the overarching
        business strategies of the companies. It would help in avoiding a siloed
        approach toward ESG, which may end up yielding only shallow information
        during the reporting process, having no real purpose in terms of
        aligning them with the business strategies of the companies within the
        scope of the question.
        <br />
        <br />
        {makeList(["Creating Long-Term Value"])}
        Ultimately, good BRSR reporting goes beyond mere regulatory
        compliance-it's about securing sustainable growth. The adoption of such
        best practices instills investor confidence, improves decision-making,
        and elevates a company's corporate reputation. Embedding ESG into core
        operations and strategy helps companies transform compliance into an
        excellent framework for long-term value creation.
      </p>
      <p>
        <div className="blog-sub-heading">FAQ’s</div>
      </p>
      <p>
        1. What is BRSR and the relevance of its content?
        <br />
        "Business Responsibility Sustainability Reporting" is a framework
        launched by ‘SEBI’ which provides information on enhancing
        "transparency" together with "accountability" for ESG 'reports.' It is
        an important term because, through 'BRSR,' companies are enabled to
        report their environmental, social, as well as 'governance' performance,
        which helps investors take informed decisions accordingly.
      </p>
      <p>
        2. Which companies are required to comply with BRSR?
        <br />
        It is also made mandatory for the top 1,000 companies based on the
        market capitalization of Indian stock exchanges. Other companies can
        also use this by volunteering to implement it for better ESG disclosures
        based on global sustainability practices.
      </p>
      <p>
        3. How do organizations improve accuracy in data in BRSR reporting?
        <br />
        Firms can improve data accuracy by using a data system, standardizing
        data metrics, internal audits, and reporting scope as well as
        methodologies prior to data collection start times.
      </p>
      <p>
        4. How does effective BRSR reporting create long-term business value?
        <br />
        For instance, effective reporting under BRSR not only bolsters
        investors' confidence, boosts effective management of risk, optimizes
        processes towards efficiency, and reinforces brand credibility, but it
        also provides opportunities for companies that align their ESG
        strategies with business strategies, making it a means of creating
        business value rather than mere regulatory requirements.
      </p>
    </div>
  );
}

export default Blog_56;
